import { createColumnHelper } from "@tanstack/react-table";
import { Icon, Loader, Popup } from "semantic-ui-react";

import { Can, ReactTable } from "components";
import { API_KEY_STATUS, API_KEY_TYPES } from "models";
import "modules/ApiKeys/components/KeysTable/KeysTable.scss";
import { ApiKeysWithShownKey } from "modules/ApiKeys/queryKeys";
import { useApiReadingKeySearch } from "modules/ApiKeys/service";
import { handleCopyToClipboard } from "util/common";
import { DATE_FORMATS_FNS, formatDate } from "util/dateHelpers";

type ApiReadingKeysTableProps = {
  handleShowApiKeyModal: (id: number) => void;
  handleRevokeApiKeyModal: (id: number) => void;
  handleRequestsLogsModal: (value: number) => void;
};

export const ApiReadingKeysTable = ({ handleShowApiKeyModal, handleRevokeApiKeyModal, handleRequestsLogsModal }: ApiReadingKeysTableProps) => {
  const { data: apiKeys, isLoading } = useApiReadingKeySearch();

  const columnHelper = createColumnHelper<ApiKeysWithShownKey>();
  const columns = [
    columnHelper.accessor(row => row.name, {
      id: "name",
      header: "Name",
      cell: info => <span>{info.renderValue()}</span>,
      size: 100
    }),
    columnHelper.accessor(row => row.company_name, {
      id: "company",
      header: "Company",
      cell: info => <span>{info.renderValue()}</span>,
      size: 100
    }),
    columnHelper.accessor(row => row, {
      id: "api_key",
      header: "Key",
      cell: ({ getValue }) => {
        const value = getValue();
        const { id, status, api_key } = value;

        if (api_key) {
          return (
            <div className="flex ApiReadingKeysTable__copy-to-clipboard" onClick={() => handleCopyToClipboard(api_key, "Api key copied to clipboard")}>
              <div className="ApiReadingKeysTable__ref-container">
                <Icon name="copy outline" className="ApiReadingKeysTable__copy-icon" />
                <Popup hoverable content={api_key} trigger={<p className="ApiReadingKeysTable__table-ellipsis">{api_key}</p>} />
              </div>
            </div>
          );
        }

        if (status === API_KEY_STATUS.Active) {
          return (
            <button className="ApiReadingKeysTable__table-button ApiReadingKeysTable__table-button-teal" onClick={() => handleShowApiKeyModal(id)}>
              <span className="ApiReadingKeys__table-text">Show the API Key </span>
            </button>
          );
        }
      },
      size: 250
    }),
    columnHelper.accessor(row => row.type, {
      id: "type",
      header: "Type",
      cell: info => {
        const type = info.renderValue();
        return <span>{type === API_KEY_TYPES.Read ? "Read" : "Write"}</span>;
      },
      size: 30
    }),
    columnHelper.accessor(row => row, {
      id: "status",
      header: "Status",
      cell: ({ renderValue }) => {
        const value = renderValue();
        if (!value) return;

        const { id, status } = value;

        if (status === API_KEY_STATUS.Active) {
          return (
            <Can I="revoke" the="api-keys">
              <button className="ApiReadingKeysTable__table-button ApiReadingKeysTable__table-button-orange" onClick={() => handleRevokeApiKeyModal(id)}>
                <span className="ApiReadingKeys__table-text">Revoke</span>
              </button>
            </Can>
          );
        }

        return (
          <button className="ApiReadingKeysTable__table-button ApiReadingKeysTable__table-button-disabled" disabled>
            <span className="ApiReadingKeys__table-text">Revoked</span>
          </button>
        );
      },
      size: 70
    }),
    columnHelper.accessor(row => row.created_by_name, {
      id: "created_by",
      header: "Created By",
      cell: info => <span>{info.renderValue()}</span>,
      size: 70
    }),
    columnHelper.accessor(row => row.created_on, {
      id: "created_on",
      header: "Created On",
      cell: info => {
        const value = info.renderValue();
        return <span>{value ? formatDate(value, DATE_FORMATS_FNS.dateMonthYear) : ""}</span>;
      },
      size: 70
    }),
    columnHelper.accessor(row => row, {
      id: "request_logs",
      header: () => (
        <div className="ApiReadingKeysTable__table-request-logs">
          <span>Request Logs</span>
        </div>
      ),
      cell: ({ renderValue }) => {
        const value = renderValue();
        if (!value?.id) return;

        return (
          <div className="ApiReadingKeysTable__table-request-cell">
            <button className="ApiReadingKeysTable__table-button ApiReadingKeysTable__table-button-teal" onClick={() => handleRequestsLogsModal(value.id)}>
              <span className="ApiReadingKeys__table-text">Request Logs </span>
            </button>
          </div>
        );
      },
      size: 50
    })
  ];

  if (isLoading)
    return (
      <div className="ApiReadingKeys__table-loader">
        <Loader active inline />
      </div>
    );

  return (
    <div className="ApiReadingKeys__table-container">
      <ReactTable columns={columns} data={apiKeys} renderEmptyRow={!apiKeys?.length} emptyRowMessage="No data" />
    </div>
  );
};
